/**
 * Решения: наступление и применение.
 *
 * `dueDecision`: не больше одного решения в год — null, если жизнь мертва,
 * решение уже висит (`pendingDecision`) или в этом возрасте одно уже
 * принято (`flags.decisionAge`). Иначе — первое наступившее по порядку
 * DECISION_DEFS (это и есть приоритет). Функция чистая: на одной жизни и
 * одном seed возвращает одно и то же решение.
 *
 * `resolveDecision`: неизвестный или недоступный вариант («починенный»
 * клиент) → `rejected: true`, жизнь возвращается без изменений (тот же
 * объект); иначе применяется эффект, ставится `flags.decisionAge` и
 * возвращаются записи журнала. При `auto: true` (таймаут, автопилот)
 * варианты из `def.silent` («ничего не изменилось») записей не дают.
 * Мёртвая жизнь не меняется никогда.
 */
import type { Dataset } from '@life/data';
import type { Rng } from '../rng';
import type { Life, LogEntry } from '../types';
import { DECISION_DEFS, FLAG_DECISION_AGE, type Decision, type DecisionCtx, type DecisionDef, type DecisionKind } from './defs';

export {
  DECISION_DEFS,
  DECISION_KINDS,
  FLAG_DECISION_AGE,
  FLAG_JOB_START_AGE,
  FLAG_RETIRED,
  FLAG_PENSION,
  FLAG_MOVE_CITY_DECIDED,
  FLAG_ABROAD_DECIDED,
  FLAG_SPONSOR,
  FLAG_SPONSOR_ISO3,
  FLAG_LOVE_ASKED_AGE,
  FLAG_CHILD_ASKED_AGE,
  FLAG_CHRONIC_DECIDED,
  FLAG_ACCIDENT_DECIDED_YEAR,
  FLAG_INVEST_ASKED_AGE,
  FLAG_RETIRE_ASKED_AGE,
  FLAG_CARE_DECIDED,
  FLAG_EVER_WORKED,
  FLAG_INTERVIEW_LEVEL,
  FLAG_TREATMENT_HEALTH,
  FLAG_TREATMENT_COST_SHARE,
  FLAG_ABROAD_CHANCE_DELTA,
  FLAG_JOB_LEVEL_BONUS,
  TREAT_COST_GDP,
  TREAT_HEALTH,
  WAIT_HEALTH,
  ABROAD_TIMEOUT_GO,
  ABROAD_MIN_AGE,
  ABROAD_MAX_AGE,
  ABROAD_MONEY_GDP,
  ABROAD_COST_GDP,
  ABROAD_BASE_CHANCE,
  ABROAD_SPONSOR_BONUS,
  LICENSE_JOB_LEVEL_BONUS,
} from './defs';
export type { Decision, DecisionCtx, DecisionDef, DecisionKind, DecisionOption } from './defs';

const DEF_BY_KIND: ReadonlyMap<DecisionKind, DecisionDef> = new Map(DECISION_DEFS.map((d) => [d.kind, d]));

export function decisionDef(kind: DecisionKind): DecisionDef {
  const def = DEF_BY_KIND.get(kind);
  if (!def) throw new RangeError(`decisionDef: unknown decision ${kind}`);
  return def;
}

/** Ключ вопроса в каталоге i18n: `decision.<kind>`. */
export function decisionPromptKey(kind: DecisionKind): string {
  return `decision.${kind}`;
}

function ctxFor(ds: Dataset, life: Life): DecisionCtx {
  return { ds, life, year: life.birthYear + life.age };
}

function alreadyDecidedThisYear(life: Life): boolean {
  return life.flags[FLAG_DECISION_AGE] === life.age;
}

/** Решение этого года или null; не больше одного в год, приоритет — порядок DECISION_DEFS. */
export function dueDecision(ds: Dataset, life: Life, rng: Rng): Decision | null {
  if (!life.alive || life.pendingDecision !== null || alreadyDecidedThisYear(life)) return null;
  const ctx = ctxFor(ds, life);
  for (const def of DECISION_DEFS) {
    if (!def.due(ctx)) continue;
    const options = def.options(ctx, rng);
    if (!options.some((o) => o.available)) continue;
    return {
      id: `${def.kind}@${life.age}`,
      kind: def.kind,
      promptKey: decisionPromptKey(def.kind),
      options,
      timeoutOption: def.timeout(ctx, options, rng),
    };
  }
  return null;
}

/** Применить выбранный вариант; недоступный или неизвестный → rejected, жизнь без изменений. */
export function resolveDecision(
  ds: Dataset,
  life: Life,
  decision: Decision,
  optionId: string,
  rng: Rng,
  opts: { auto?: boolean } = {},
): { life: Life; entries: LogEntry[]; rejected: boolean } {
  const option = decision.options.find((o) => o.id === optionId);
  if (!life.alive || !option || !option.available || !DEF_BY_KIND.has(decision.kind)) {
    return { life, entries: [], rejected: true };
  }
  const def = decisionDef(decision.kind);
  const applied = def.apply(ctxFor(ds, life), optionId, rng);
  const next: Life = {
    ...applied.life,
    pendingDecision: null,
    flags: { ...applied.life.flags, [FLAG_DECISION_AGE]: life.age },
  };
  const silent = Boolean(opts.auto) && (def.silent?.includes(optionId) ?? false);
  return { life: next, entries: silent ? [] : applied.entries, rejected: false };
}
